import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CreatableSelect from 'react-select/creatable';

import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import { toastSuccess } from '@components/Toast';

import SelectColor from '@features/assets/components/CommonComponents/SelectColor';
import UploadFiles from '@features/assets/components/CommonComponents/UploadFiles';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { getAllTags } from '@store/reducers';
import { fetchAllTags } from '@store/supplier/action';

import { onAddArtwork, onUpdateArtwork } from '@services/assetsService';

import { checkUserType } from '@utils/Util';

const AddArtworkModal = ({ artWorkResponse, saveType, onCloseModal, artworkId, onActionSucces }) => {
	const [isLoading, setIsloading] = useState(false);
	const [searchTerm] = useState('');
	const [selectedListType, setSelectedListType] = useState('ARTWORK_MOOD_TAG');
	const [selectedArtWorkResponse, setSelectedArtworkResponse] = useState(null);
	const [multipleImageData, setMultipleImageData] = useState([]);
	const [allFilesAndImages, setAllFilesAndImages] = useState([]);
	const [selectedColorList, setSelectedColorList] = useState([]);
	const [pantoneColorIds, setPantoneColorIds] = useState([]);
	const [documentIds, setDocumentIds] = useState([]);
	const [error, setError] = useState(false);
	const dispatch = useDispatch();

	const defaultArtworkResponse = {
		documentResponseList: [],
		moodTagResponse: null,
		artworkPatternTagResponse: null,
		inspiredFrom: '',
		tagResponseList: [],
		colorResponseList: [],
		remarks: ''
	};

	useEffect(() => {
		if (saveType === 'add') {
			setSelectedArtworkResponse(defaultArtworkResponse);
		} else {
			const formattedArtworkResponse = {
				...artWorkResponse,
				tagResponseList: artWorkResponse?.tagResponseList?.map((tag) => ({
					id: tag.id,
					text: tag.text
				}))
			};
			setSelectedArtworkResponse(formattedArtworkResponse || defaultArtworkResponse);
		}

		if (saveType === 'edit' && artWorkResponse?.colorResponseList?.length !== 0) {
			setSelectedColorList(artWorkResponse?.colorResponseList ?? []);
			let allPantoneColorIds = artWorkResponse?.colorResponseList?.map((item) => item?.id);
			setPantoneColorIds(allPantoneColorIds);
		}
	}, [artWorkResponse, saveType]);

	useEffect(() => {
		if (saveType === 'edit' && selectedArtWorkResponse?.documentResponseList?.length !== 0) {
			setAllFilesAndImages(selectedArtWorkResponse?.documentResponseList);
			let alldocumentIds = selectedArtWorkResponse?.documentResponseList?.map((item) => item.id);
			setDocumentIds(alldocumentIds);
		} else {
			setAllFilesAndImages([]);
		}
	}, [selectedArtWorkResponse]);

	let allTags = useSelector(getAllTags)?.allTags?.map((item) => {
		return {
			value: item.id,
			label: item.text
		};
	});

	let allOtherTags = useSelector(getAllTags)?.allTags?.map((item) => {
		return {
			id: item.id,
			value: item.text
		};
	});

	const onTagsChange = (selected) => {
		let tags = selected.map((item) => {
			if (item.value === item.label) {
				return {
					text: item.label ?? item.text
				};
			} else {
				return {
					id: item.value ?? item.id,
					text: item.label ?? item.text
				};
			}
		});
		setSelectedArtworkResponse({ ...selectedArtWorkResponse, tagResponseList: tags });
	};

	const onAddListType = (value, type) => {
		let selectedItem = { text: value.value };

		if (value?.id) {
			selectedItem['id'] = value.id;
		}

		const updateMap = {
			ARTWORK_MOOD_TAG: 'moodTagResponse',
			ARTWORK_PATTERN_TAG: 'artworkPatternTagResponse'
		};

		setSelectedArtworkResponse({ ...selectedArtWorkResponse, [updateMap[type]]: selectedItem });
	};

	const handleMultipleImageDataChange = (imageData) => {
		setMultipleImageData(imageData);
	};

	const validateForm = () => {
		let cloneError = {};
		if (!selectedArtWorkResponse?.moodTagResponse) {
			cloneError.mood = 'Field is required';
		}
		if (!selectedArtWorkResponse?.artworkPatternTagResponse) {
			cloneError.pattern = 'Field is required';
		}

		const firstImage = multipleImageData.find((img) => img.docType === 'ASSETS_IMAGE_S1');
		if (!firstImage) {
			cloneError.image = 'Front image is required.';
		}

		setError(cloneError);
		return Object.keys(cloneError).filter((key) => cloneError[key] !== undefined).length > 0;
	};

	const handleImageIdsChange = (ids) => {
		setDocumentIds(ids);
	};

	const onSubmit = async () => {
		if (!validateForm()) {
			let body = {
				inspiredFrom: selectedArtWorkResponse?.inspiredFrom,
				remarks: selectedArtWorkResponse?.remarks,
				mood: selectedArtWorkResponse?.moodTagResponse,
				artworkPattern: selectedArtWorkResponse?.artworkPatternTagResponse,
				pantoneColorIds: pantoneColorIds,
				documentIds: documentIds,
				tags: selectedArtWorkResponse?.tagResponseList
			};
			try {
				dispatch(openLoader());
				const responseData =
					saveType === 'edit' ? await onUpdateArtwork(body, artworkId) : await onAddArtwork(body);
				onActionSucces(responseData);
				toastSuccess('Successfully updated');
				dispatch(closeLoader());
				onCloseModal();
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	useEffect(() => {
		setIsloading(true);
		dispatch(fetchAllTags(searchTerm, selectedListType)).finally(() => {
			setIsloading(false);
		});
	}, [searchTerm, selectedListType, dispatch]);

	return (
		<ModalContainer className="add-Sketch-modal" onCloseModal={() => {}}>
			<div className="common-popup add-Sketch-modal">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte d-flex align-items-center">
						<h3 className="semibold-16 mb-0">{saveType === 'edit' ? 'Edit' : 'Add'} Artwork</h3>
					</div>
					<div className="close-btn" onClick={onCloseModal}>
						<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
					</div>
				</div>
				<div className="common-popup-body scroll-y-label">
					<UploadFiles
						type="ARTWORK"
						itemId={selectedArtWorkResponse?.id || null}
						onImageIdsChange={handleImageIdsChange}
						onMultipleImageDataChange={handleMultipleImageDataChange}
						allFilesAndImages={allFilesAndImages}
						error={error.image}
					/>
					<div className="pom-form artwork-form">
						<div className="row">
							<div className="col-md-6 pr-2">
								<div
									className="form-group"
									onClick={() => setSelectedListType('ARTWORK_MOOD_TAG')}
								>
									<label htmlFor="collection">Mood*</label>
									<CustomDropdown
										isLoading={isLoading}
										isShowLabel={false}
										toolTipLength={18}
										items={allOtherTags}
										selectedItem={selectedArtWorkResponse?.moodTagResponse?.text}
										isAddNewOnInline={true}
										onItemClick={(value) => onAddListType(value, 'ARTWORK_MOOD_TAG')}
									/>{' '}
									{error.mood && <p className="error">{error.mood}</p>}
								</div>
							</div>
							<div className="col-md-6 pl-2">
								<div
									className="form-group "
									onClick={() => setSelectedListType('ARTWORK_PATTERN_TAG')}
								>
									<label htmlFor="rise">Artwork Pattern*</label>
									<CustomDropdown
										isLoading={isLoading}
										isShowLabel={false}
										toolTipLength={18}
										items={allOtherTags}
										selectedItem={
											selectedArtWorkResponse?.artworkPatternTagResponse?.text
										}
										isAddNewOnInline={checkUserType('ADMIN')}
										onItemClick={(value) => onAddListType(value, 'ARTWORK_PATTERN_TAG')}
									/>{' '}
									{error.pattern && <p className="error">{error.pattern}</p>}
								</div>
							</div>
							<div className="col-md-6 pr-2">
								<div className="form-group">
									<label htmlFor="collection">Inspired from</label>
									<input
										type="text"
										className="w-100"
										onChange={(e) =>
											setSelectedArtworkResponse({
												...selectedArtWorkResponse,
												inspiredFrom: e.target.value
											})
										}
										value={selectedArtWorkResponse?.inspiredFrom}
									/>
								</div>
							</div>
							<div className="col-md-6 pl-2">
								<SelectColor
									selectedColorList={selectedColorList}
									setSelectedColorList={setSelectedColorList}
									setPantoneColorIds={setPantoneColorIds}
								/>
							</div>

							<div className="col-md-6 pr-2">
								<div
									className="form-group no-shadow-react-select"
									onClick={() => setSelectedListType('ARTWORK_TAGS')}
								>
									<label htmlFor="inspired">Tag</label>
									<CreatableSelect
										isMulti
										value={selectedArtWorkResponse?.tagResponseList?.map((tag) => ({
											value: tag.id,
											label: tag.text
										}))}
										onChange={onTagsChange}
										options={allTags}
									/>
								</div>
							</div>

							<div className="col-md-6 pl-2">
								<div className="form-group">
									<label htmlFor="collection">Remarks</label>
									<input
										type="text"
										className="w-100"
										onChange={(e) =>
											setSelectedArtworkResponse({
												...selectedArtWorkResponse,
												remarks: e.target.value
											})
										}
										value={selectedArtWorkResponse?.remarks}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="modal-footer pt-0">
					<div className="footer-buttons text-right">
						<button className="button gray-outline size32 ml-2" onClick={onCloseModal}>
							Cancel
						</button>
						<button className="button size32 ml-2" onClick={onSubmit}>
							Submit
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default AddArtworkModal;
